import * as React from 'react';
import {useDangerousHooks, TypeImplementation} from '@packages/systems/wfdl';
import {TText} from '@packages/systems/wfdl-ast';
import {emotionStyled} from '@packages/utilities/styled';
import {panelOpened} from '@packages/systems/panels';
import {Note, NoteLink, ArrowRightIcon} from '@packages/systems/spring';
import {BaseButton as Button} from '@packages/systems/design-system/Button';
import {
  getSearchResultPage,
  getPageBySlug,
} from '@packages/shared/utils/PageTreeUtils';
import {
  useDesignerSelector,
  useDispatch,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {selectDesignerAbility} from '@packages/systems/designer-abilities';

const ButtonContainer = emotionStyled('div')({
  display: 'flex',
});

const NoteWrapper = emotionStyled('div')({
  marginTop: 12,
});

const NoteContentWrapper = emotionStyled('div')({
  display: 'inline',
});

const IconWrapper = emotionStyled('div')({
  marginLeft: 5,
});

const selectCurrentPageId = (state: DesignerStores) =>
  state.DesignerStore.currentPageId;
const selectPageStore = (state: DesignerStores) => state.PageStore;
const selectCanAccessSiteSearch = selectDesignerAbility('canAccessSiteSearch');

const SearchFormTypeRender = () => {
  const [hovered, isHovered] = React.useState(false);
  const {useUnsafeActionCreators} = useDangerousHooks();
  const hooksDispatch = useDispatch();

  const {
    PageActionCreators: {switchPage},
    PageManagerActionCreators: {openSettings},
    // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
  } = useUnsafeActionCreators();

  const currentPageId = useDesignerSelector(selectCurrentPageId);
  const designerPageStore = useDesignerSelector(selectPageStore);
  const canAccessSiteSearch = useDesignerSelector(selectCanAccessSiteSearch);
  const searchResultPage = getSearchResultPage(designerPageStore);
  const searchResultPageId = searchResultPage?.id;
  const KEY = 'search';
  const sluggedPage = getPageBySlug(designerPageStore, KEY);

  const handleGoToSearchResultsPageClick = () => {
    searchResultPage &&
      hooksDispatch(
        switchPage({
          id: searchResultPageId,
        })
      );
  };

  const isCurrentSearchResultsPage =
    searchResultPage && currentPageId && searchResultPageId === currentPageId;

  const existingPageNeedsChangeSlug =
    sluggedPage && sluggedPage.utilKey !== KEY ? sluggedPage : false;

  const handleExistingSlugChangeClick = () => {
    if (!sluggedPage) {
      return;
    }
    hooksDispatch(panelOpened('pages'));
    hooksDispatch(openSettings(sluggedPage.id, 'slug'));
  };

  if (!canAccessSiteSearch) {
    return null;
  }

  return (
    <>
      <ButtonContainer>
        <Button
          hover={hovered}
          onMouseEnter={() => isHovered(true)}
          onMouseLeave={() => isHovered(false)}
          sizing="large"
          style={{lineHeight: 'initial'}}
          full
          onClick={handleGoToSearchResultsPageClick}
          disabled={isCurrentSearchResultsPage || existingPageNeedsChangeSlug}
          data-automation-id="go-to-search-results-page"
        >
          {isCurrentSearchResultsPage
            ? 'On Search Results Page'
            : 'Go to Search Results Page'}
          {!isCurrentSearchResultsPage && (
            <IconWrapper>
              <ArrowRightIcon size={12} />
            </IconWrapper>
          )}
        </Button>
      </ButtonContainer>
      {existingPageNeedsChangeSlug && (
        <NoteWrapper>
          <Note
            isNested
            colorVariant="warning"
            hasIcon={false}
            style={{marginBlockStart: 8}}
          >
            <NoteContentWrapper>
              To enable search, update the slug of your
              {' ' + existingPageNeedsChangeSlug.get('name') + ' '}
              page to something other than <code>/search</code>.
            </NoteContentWrapper>
            <NoteLink
              data-automation-id="change-page-slug"
              onClick={handleExistingSlugChangeClick}
            >
              Change Page Slug
            </NoteLink>
          </Note>
        </NoteWrapper>
      )}
    </>
  );
};

export const SearchFormType = TypeImplementation.Alias({
  type: TText,
  render: SearchFormTypeRender,
});
